// GENERATED by scripts/generate-design-tokens.mjs from docs/design/tokens.json.
// Do not edit by hand: change tokens.json and run `npm run tokens:generate`.
// tests/web/design-tokens.test.ts fails the build when this file drifts from tokens.json.
//
// The two layers are deliberate. The STANDARD tables (standardColors, typeScale,
// spacing, radii, shadows, motion, touch) are what new code reads: they carry
// the names in docs/design/os-design-reference.md. The LEGACY exports (colors,
// modes, careStatus, gradients, type, space, z, breakpoints, avatarGradients)
// keep the names the app imported before 2026-11-04 so nothing breaks; their
// values now come from the standard where it has a token (tokens.json legacy)
// and are retired surface by surface in Phase 1.
//
// Why both CSS custom properties AND TS constants: CSS vars are the idiom for
// stylesheets; TS constants are typeable in component code and in the few
// places (charts, canvas, inline styles) that need a literal value.

// ─── The standard's color tokens, light and dark ────────────────────────────

export const standardColors = {
  heading: { light: "#022c51", dark: "#e1e9f3" },
  "text-1": { light: "#14212f", dark: "#e8eef5" },
  "text-2": { light: "#44566a", dark: "#b9c6d4" },
  "text-3": { light: "#52637a", dark: "#8fa0b3" },
  link: { light: "#2a6da8", dark: "#8cc2e6" },
  "surface-page": { light: "#f3f5f8", dark: "#0c1828" },
  "surface-card": { light: "#ffffff", dark: "#13243a" },
  "surface-raise": { light: "#f7f9fb", dark: "#182c45" },
  "surface-tint": { light: "#e8f2f9", dark: "#18324f" },
  "surface-seg": { light: "#e6ebf1", dark: "#0f1f33" },
  "navy-surface": { light: "#022c51", dark: "#022c51" },
  "on-fill": { light: "#ffffff", dark: "#ffffff" },
  "on-navy-accent": { light: "#8cc2e6", dark: "#8cc2e6" },
  "navy-button": { light: "#022c51", dark: "#1f4267" },
  line: { light: "#e6ebf0", dark: "#24384f" },
  control: { light: "#7a8ca0", dark: "#7d97b3" },
  focus: { light: "#2a6da8", dark: "#8cc2e6" },
  good: { light: "#1f8a4c", dark: "#52c78a" },
  "good-ink": { light: "#17653a", dark: "#7fdca5" },
  "good-bg": { light: "#e6f4ec", dark: "#163a2a" },
  warn: { light: "#b45309", dark: "#eba45e" },
  "warn-ink": { light: "#8a4a10", dark: "#f2c08e" },
  "warn-bg": { light: "#fdf3e6", dark: "#3a2c17" },
  bad: { light: "#c0392b", dark: "#ef7d70" },
  "bad-bg": { light: "#fdecea", dark: "#3d1f22" },
  "bad-solid": { light: "#c0392b", dark: "#b3352a" },
  "inverse-surface": { light: "#14212f", dark: "#e8eef5" },
  "inverse-text": { light: "#ffffff", dark: "#14212f" },
  "inverse-link": { light: "#8cc2e6", dark: "#1f5a8a" },
  "inverse-good": { light: "#7fdca5", dark: "#17653a" },
  opp: { light: "#2a6da8", dark: "#9cc8e6" },
  "opp-bg": { light: "#deeef7", dark: "#173a57" },
  proc: { light: "#8a5a16", dark: "#f0c98a" },
  "proc-bg": { light: "#fdf0d1", dark: "#3a2f12" },
  care: { light: "#1f6a52", dark: "#8fd6b8" },
  "care-bg": { light: "#dcefe8", dark: "#123a30" },
  yellow: { light: "#fed766", dark: "#fed766" },
} as const;

export type StandardColorToken = keyof typeof standardColors;

/** The CSS custom property for a standard token: cssVar("text-2") → "var(--text-2)". */
export function cssVar(token: StandardColorToken): string {
  return `var(--${token})`;
}

// ─── Legacy color names (values from the standard where it has one) ─────────

export const colors = {
  navy: "#022c51",
  navyDeep: "#011e38",
  navyMid: "#033d70",
  navySurface: "#022c51",
  blue: "#4e98c6",
  blueLight: "#5ba3d0",
  blueDark: "#3a7ea8",
  yellow: "#fed766",
  yellowDeep: "#e8c24a",
  white: "#ffffff",
  lightGray: "#f3f5f8",
  midGray: "#e6ebf0",
  darkGray: "#44566a",
  darkGrayLight: "#52637a",
  blueTint: "#e8f2f9",
  blueTintDeep: "#c8dff0",
  success: "#1f8a4c",
  successBg: "#e6f4ec",
  warning: "#b45309",
  warningBg: "#fdf3e6",
  danger: "#c0392b",
  dangerHover: "#a5322a",
  dangerBg: "#fdecea",
  dangerBorder: "#fecaca",
  pinnedBg: "#fff7ce",
  attention: "#b45309",
  attentionTint: "#fdf3e6",
  attentionBorder: "#f0d3a8",
  fg1: "#14212f",
  fg2: "#44566a",
  fg3: "#52637a",
  fgMuted: "#52637a",
  fgOnNavy: "#ffffff",
  fgOnNavy2: "rgba(255, 255, 255, 0.75)",
  fgOnNavy3: "rgba(255, 255, 255, 0.55)",
  navbarBorder: "rgba(255, 255, 255, 0.06)",
  navbarLinkDim: "rgba(255, 255, 255, 0.78)",
  navbarLinkHoverBg: "rgba(255, 255, 255, 0.08)",
  navbarSearchBg: "rgba(255, 255, 255, 0.08)",
} as const;

// ─── Workflow modes: Opportunity / Processing / Ongoing Care ────────────────
//
// base = chrome (board headers, mode stripes); soft = badge fill (--opp-bg
// and friends); deep = badge text (--opp and friends).

export const modes = {
  opp: {
    base: "#4e98c6",
    soft: "#deeef7",
    deep: "#2a6da8",
  },
  proc: {
    base: "#c69220",
    soft: "#fdf0d1",
    deep: "#8a5a16",
  },
  care: {
    base: "#2a8e6e",
    soft: "#dcefe8",
    deep: "#1f6a52",
  },
} as const;

export type ModeKey = keyof typeof modes;

// ─── Ongoing Care renewal status (mirrors --st-*) ───────────────────────────

export const careStatus = {
  overdue: "#c0392b",
  due: "#b45309",
  review: "#2a6da8",
  reviewQuiet: "#9db8cf",
  running: "#1f8a4c",
} as const;

export type CareStatusToken = keyof typeof careStatus;

// ─── Gradients ──────────────────────────────────────────────────────────────

export const gradients = {
  navy: "linear-gradient(160deg, #011e38 0%, #022c51 50%, #033d70 100%)",
  button: "linear-gradient(135deg, #3374a3 0%, #2a6da8 50%, #1f5a8a 100%)",
  yellow: "linear-gradient(135deg, #e8c24a 0%, #fed766 100%)",
} as const;

// ─── Font stacks ────────────────────────────────────────────────────────────

export const fonts = {
  heading: "\"Gotham\", \"Montserrat\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif",
  body: "\"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif",
  mono: "\"SF Mono\", ui-monospace, Menlo, Monaco, Consolas, monospace",
} as const;

// ─── Gotham letter-spacing (tracking) scale ─────────────────────────────────
//
// Tracking scales INVERSELY with font-size, because caps need proportionally
// more air as they get smaller (Grant, 2026-08-23). Uppercase gets the full
// value; mixed case about half. The guard in tests/web/design-tokens.test.ts
// holds every sized Gotham run to this floor.

export const tracking = {
  /** Uppercase (headings, eyebrows, section headings). */
  caps: [
    { maxPx: 10.5, em: 0.12 },
    { maxPx: 12.5, em: 0.1 },
    { maxPx: 14, em: 0.08 },
    { maxPx: 17, em: 0.06 },
    { maxPx: 23, em: 0.04 },
    { maxPx: 29, em: 0.03 },
    { maxPx: Number.POSITIVE_INFINITY, em: 0.01 },
  ],
  /** Mixed / sentence case — names, quotes, sentence-case titles. */
  mixed: [
    { maxPx: 10.5, em: 0.06 },
    { maxPx: 12.5, em: 0.05 },
    { maxPx: 14, em: 0.04 },
    { maxPx: 17, em: 0.03 },
    { maxPx: 29, em: 0.02 },
    { maxPx: Number.POSITIVE_INFINITY, em: 0.01 },
  ],
} as const;

/**
 * The tracking (letter-spacing, in em) a Gotham run of `sizePx` should use.
 * `uppercase` defaults to true because the OS's heading font is uppercase by
 * doctrine nearly everywhere.
 */
export function trackingForSize(sizePx: number, uppercase = true): number {
  const scale = uppercase ? tracking.caps : tracking.mixed;
  for (const step of scale) {
    if (sizePx <= step.maxPx) return step.em;
  }
  // Unreachable — the last step is Infinity — but keeps the return total.
  return scale[scale.length - 1]!.em;
}

// ─── Type scale (os-design-reference.md section 3) ──────────────────────────
//
// [size px, line-height, tracking, weight, case] per role, as tokens.json
// holds them. No size outside this table.

export const typeScale = {
  "page-title": { size: 26, lineHeight: 1.15, tracking: "0.03em", weight: "800", case: "caps" },
  "record-title": { size: 26, lineHeight: 1.15, tracking: "0.03em", weight: "800", case: "caps" },
  "page-title-phone": { size: 22, lineHeight: 1.15, tracking: "0.04em", weight: "800", case: "caps" },
  "record-title-phone": { size: 22, lineHeight: 1.15, tracking: "0.04em", weight: "800", case: "caps" },
  "dialog-title": { size: 18, lineHeight: 1.25, tracking: "0.04em", weight: "800", case: "caps" },
  "panel-title": { size: 16, lineHeight: 1.3, tracking: "0.05em", weight: "800", case: "caps" },
  "section-heading": { size: 12, lineHeight: 1.3, tracking: "0.10em", weight: "800", case: "caps" },
  eyebrow: { size: 10.5, lineHeight: 1, tracking: "0.12em", weight: "800", case: "caps" },
  "big-number": { size: 28, lineHeight: 1.1, tracking: "0.01em", weight: "800", case: "tabular" },
  "big-number-phone": { size: 22, lineHeight: 1.1, tracking: "0.02em", weight: "800", case: "tabular" },
  "calendar-day": { size: 18, lineHeight: 1.2, tracking: "0.02em", weight: "800", case: "" },
  "calendar-day-phone": { size: 16, lineHeight: 1.2, tracking: "0.02em", weight: "800", case: "" },
  "button-primary": { size: 13, lineHeight: 1, tracking: "0", weight: "700", case: "sentence case, Open Sans (Grant, 2026-11-03)" },
  "button-plain": { size: 13, lineHeight: 1, tracking: "0", weight: "600", case: "sentence case, Open Sans" },
  "button-small": { size: 11, lineHeight: 1, tracking: "0.10em", weight: "600", case: "caps" },
  "queue-verdict": { size: 18, lineHeight: 1.3, tracking: "0", weight: "600 Open Sans", case: "" },
  "row-name": { size: 15, lineHeight: 1.3, tracking: "0", weight: "700 Open Sans", case: "" },
  "page-sentence": { size: 15, lineHeight: 1.6, tracking: "0", weight: "400 Open Sans", case: "" },
  body: { size: 14, lineHeight: 1.6, tracking: "0", weight: "400 Open Sans", case: "" },
  "second-line": { size: 13.5, lineHeight: 1.5, tracking: "0", weight: "400 Open Sans", case: "" },
  tab: { size: 14, lineHeight: 1, tracking: "0", weight: "600 Open Sans", case: "" },
  small: { size: 13, lineHeight: 1.4, tracking: "0", weight: "400 or 600 Open Sans", case: "" },
  meta: { size: 12.5, lineHeight: 1.4, tracking: "0", weight: "400 or 600 Open Sans", case: "" },
  "field-label": { size: 14, lineHeight: 1.3, tracking: "0", weight: "600 Open Sans", case: "" },
  input: { size: 16, lineHeight: 1, tracking: "0", weight: "400 Open Sans", case: "" },
  badge: { size: 11.5, lineHeight: 1, tracking: "0", weight: "700 Open Sans", case: "" },
  "count-pill": { size: 11, lineHeight: 1, tracking: "0", weight: "700 Open Sans", case: "" },
} as const;

export type TypeRole = keyof typeof typeScale;

// Legacy role names, read from the same table.
export const type = {
  pageH1: { size: 26, lineHeight: 1.15, letterSpacing: "0.03em", weight: 800 as const, textTransform: "uppercase" as const },
  zoneTitle: { size: 12, lineHeight: 1.3, letterSpacing: "0.10em", weight: 800 as const, textTransform: "uppercase" as const },
  bigNumber: { size: 28, lineHeight: 1.1, letterSpacing: "0.01em", weight: 800 as const },
  body: { size: 14, lineHeight: 1.6, weight: "400 Open Sans" },
  bodyCompact: { size: 13, lineHeight: 1.4, weight: "400 or 600 Open Sans" },
  caption: { size: 12.5, lineHeight: 1.4, weight: "400 or 600 Open Sans" },
  eyebrow: { size: 10.5, lineHeight: 1, letterSpacing: "0.12em", weight: 800 as const, textTransform: "uppercase" as const },
  navLabel: { size: 11.5, letterSpacing: "0.1em", weight: 800 as const, textTransform: "uppercase" as const },
  sidebarLabel: { size: 10.5, letterSpacing: "0.12em", weight: 800 as const, textTransform: "uppercase" as const },
  button: { size: 13, lineHeight: 1, weight: 700 as const },
} as const;

// ─── Spacing (section 4) ────────────────────────────────────────────────────

export const spacing = {
  /** Between cards, sections and inside layouts. */
  steps: [4, 8, 12, 16, 20, 24, 28, 32, 40, 48, 64, 96],
  /** Inside one component only. */
  fine: [2, 6, 10, 14],
} as const;

// Legacy keyed scale (0..10). The keys are names code already uses; their
// values are unchanged. New code reads `spacing.steps`.
export const space = {
  "0": 0,
  "1": 4,
  "2": 8,
  "3": 12,
  "4": 16,
  "5": 20,
  "6": 24,
  "7": 32,
  "8": 40,
  "9": 56,
  "10": 80,
} as const;

// ─── Radii (section 5): 4, 8, 10, 14, 999 and nothing else ──────────────────

export const radii = {
  checkbox: 4,
  control: 8,
  tile: 10,
  card: 14,
  pill: 999,
  xs: 4 /* legacy name */,
  sm: 8 /* legacy name */,
  md: 10 /* legacy name */,
  lg: 14 /* legacy name */,
} as const;

// ─── Shadows (section 5), navy-tinted in light ──────────────────────────────

export const shadows = {
  sh1: "0 1px 2px rgba(2,44,81,.06), 0 2px 10px rgba(2,44,81,.05)",
  sh2: "0 2px 4px rgba(2,44,81,.06), 0 10px 28px rgba(2,44,81,.10)",
  sh3: "0 20px 60px rgba(2,44,81,.30)",
  sm: "0 1px 2px rgba(2,44,81,.06), 0 2px 10px rgba(2,44,81,.05)",
  md: "0 2px 4px rgba(2,44,81,.06), 0 10px 28px rgba(2,44,81,.10)",
  lg: "0 20px 60px rgba(2,44,81,.30)",
  hover: "0 2px 4px rgba(2,44,81,.06), 0 10px 28px rgba(2,44,81,.10)",
  button: "0 3px 10px rgba(2,44,81,.25)",
  blueGlow: "0 8px 24px rgba(78, 152, 198, 0.18), 0 2px 8px rgba(2, 44, 81, 0.08)",
} as const;

export const shadowsDark = {
  sh1: "0 1px 2px rgba(0,0,0,.4), 0 2px 10px rgba(0,0,0,.25)",
  sh2: "0 2px 6px rgba(0,0,0,.45), 0 12px 30px rgba(0,0,0,.4)",
  sh3: "0 20px 60px rgba(0,0,0,.6)",
} as const;

// ─── Motion (section 5) ─────────────────────────────────────────────────────

export const motion = {
  fast: "150ms cubic-bezier(0.16, 1, 0.3, 1)",
  base: "200ms cubic-bezier(0.16, 1, 0.3, 1)",
  slow: "250ms cubic-bezier(0.16, 1, 0.3, 1)",
  easeOut: "cubic-bezier(0.16, 1, 0.3, 1)",
  durFast: "150ms",
  durMed: "250ms",
  durSlow: "250ms",
  maxMs: 250,
} as const;

// ─── Touch and focus ────────────────────────────────────────────────────────

export const touch = {
  under1024: 44,
  desktopMin: 24,
  controlBorderPx: 1.5,
  pillHeight: 32,
  pillHeightUnder1024: 44,
} as const;

// ─── Z-index layers ─────────────────────────────────────────────────────────

export const z = {
  nav: 40,
  sidebar: 30,
  dropdown: 200,
  modal: 1000,
  toast: 1100,
  tweaks: 100,
} as const;

// ─── Breakpoints ────────────────────────────────────────────────────────────
//
// md / lg / xl are the standard's phone, tablet and laptop edges; sm,
// collapseSidebar and contentMax are the app's own (tokens.json legacy.ts).

export const breakpoints = {
  sm: 500,
  md: 768,
  lg: 1024,
  collapseSidebar: 1100,
  xl: 1280,
  contentMax: 1440,
  readingWidth: 720,
  standardWidth: 1280,
} as const;

// ─── Per-staff avatar gradients ─────────────────────────────────────────────

export const avatarGradients = {
  default: "linear-gradient(135deg, #5ba3d0, #3a7ea8)",
  grant: "linear-gradient(135deg, #b88dd9, #7e5ca5)",
  joy: "linear-gradient(135deg, #5ba3d0, #3a7ea8)",
  brian: "linear-gradient(135deg, #6bc084, #3a8d52)",
  donna: "linear-gradient(135deg, #f48fb1, #c2185b)",
  mike: "linear-gradient(135deg, #e8b74a, #c69220)",
  nate: "linear-gradient(135deg, #ff8a65, #d84315)",
} as const;

// ─── Convenience grouped export ─────────────────────────────────────────────

export const tokens = {
  standardColors,
  colors,
  modes,
  careStatus,
  gradients,
  fonts,
  tracking,
  typeScale,
  type,
  spacing,
  space,
  radii,
  shadows,
  shadowsDark,
  motion,
  touch,
  z,
  breakpoints,
  avatarGradients,
} as const;

export type DesignTokens = typeof tokens;
